"use client";

import Link from "next/link";
import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Service } from "@/data/serviceDetails";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Plan {
  name: string;
  price: string;
  period: string;
  subtitle: string;
  services: Service[];
  popular: boolean;
}

interface PricingCardsProps {
  plans: Plan[];
  ctaAction?: () => void;
  hideCta?: boolean;
}

const ServiceModal = ({
  service,
  onClose,
  t,
}: {
  service: Service | null;
  onClose: () => void;
  t: (key: string) => string;
}) => (
  <Dialog open={!!service} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="sm:max-w-lg rounded-[20px] max-h-[85vh] overflow-y-auto">
      <DialogHeader className="text-center sm:text-center">
        <DialogTitle className="font-heading text-2xl">
          {service?.name}
        </DialogTitle>
        {service?.subtitle && (
          <p className="font-body text-base text-accent-blue font-medium pt-1">
            {service.subtitle}
          </p>
        )}
      </DialogHeader>
      <div className="space-y-4 pt-2">
        {service?.description.split("\n\n").map((paragraph, i) => (
          <p
            key={i}
            className="font-body text-sm text-foreground/80 leading-relaxed text-center"
          >
            {paragraph}
          </p>
        ))}
        {service?.conditions && (
          <div className="pt-2">
            <p className="font-heading font-bold text-xs uppercase tracking-wider text-foreground text-center mb-2">
              {t("pricing.commonlyTreatedConditions")}
            </p>
            <p className="font-body text-sm text-foreground/70 text-center leading-relaxed">
              {service.conditions}
            </p>
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

const PricingCards = ({ plans, ctaAction, hideCta }: PricingCardsProps) => {
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  return (
    <>
      {/* Cards grid — popular card lifts above peers on desktop */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:items-stretch">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`relative rounded-[24px] flex flex-col h-full transition-all duration-300 ${
              plan.popular
                ? /* Featured: gradient fill, elevated, extra padding */
                  "bg-gradient-to-br from-accent-blue to-primary shadow-2xl shadow-accent-blue/30 z-10 p-6 border-2 border-white/10"
                : /* Standard: white card, subtle hover lift */
                  "bg-surface-card shadow-sm hover:shadow-xl hover:-translate-y-1 border-2 border-transparent hover:border-accent-blue/20 p-6"
            }`}
          >
            {/* ── Most Popular badge ─────────────────────── */}
            {plan.popular && (
              <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-accent-blue text-xs font-button font-bold px-5 py-1.5 rounded-full shadow-md whitespace-nowrap">
                {t("pricing.mostPopular")}
              </span>
            )}

            {/* ── 1. Plan name + subtitle ────────────────── */}
            <div className="mt-2 mb-5">
              <h3
                className={`font-heading font-bold text-xl mb-1.5 ${
                  plan.popular ? "text-white" : "text-foreground"
                }`}
              >
                {plan.name}
              </h3>
              <p
                className={`font-body text-xs leading-relaxed ${
                  plan.popular ? "text-white/65" : "text-foreground/50"
                }`}
              >
                {plan.subtitle}
              </p>
            </div>

            {/* ── 2. Price block ─────────────────────────── */}
            <div
              className={`flex flex-col gap-1 mb-6 pb-6 border-b ${
                plan.popular ? "border-white/20" : "border-foreground/10"
              }`}
            >
              {/* Large dominant price */}
              <span
                className={`font-heading font-extrabold leading-none ${
                  plan.popular ? "text-white text-6xl" : "text-foreground text-5xl"
                }`}
              >
                {plan.price}
              </span>
              {/* Period label below price */}
              <span
                className={`font-body text-xs uppercase tracking-wide ${
                  plan.popular ? "text-white/55" : "text-foreground/40"
                }`}
              >
                {plan.period}
              </span>
            </div>

            {/* ── 3. Feature list (checkmark rows) ──────── */}
            <div className="flex flex-col gap-3 flex-1 mb-7">
              {plan.services.map((service) => (
                <button
                  key={service.name}
                  onClick={() => setSelectedService(service)}
                  className="flex items-center gap-3 text-left w-full transition-opacity duration-150 hover:opacity-70"
                  title="Click to learn more"
                >
                  {/* Northeast arrow — signals clickable/expandable */}
                  <span
                    className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                      plan.popular ? "bg-white/20" : "bg-accent-blue/10"
                    }`}
                  >
                    <ArrowUpRight
                      className={`w-3 h-3 ${
                        plan.popular ? "text-white" : "text-accent-blue"
                      }`}
                    />
                  </span>
                  <span
                    className={`font-body text-sm underline-offset-2 decoration-dotted hover:underline ${
                      plan.popular ? "text-white/90" : "text-foreground/75"
                    }`}
                  >
                    {service.name}
                  </span>
                </button>
              ))}
            </div>

            {/* ── 4. Full-width CTA ──────────────────────── */}
            {!hideCta && ctaAction ? (
              <Button
                onClick={ctaAction}
                className={`w-full rounded-full py-3 font-button text-sm transition-all duration-200 mt-auto ${
                  plan.popular
                    ? "bg-white text-accent-blue hover:bg-white/90 shadow-lg"
                    : "bg-accent-blue hover:bg-accent-blue/90 text-white cta-hover cta-hover-no-ring"
                }`}
              >
                {t("pricing.getStarted")}
              </Button>
            ) : !hideCta ? (
              <Link href="/contact" className="w-full mt-auto block">
                <Button
                  className={`w-full rounded-full py-3 font-button text-sm transition-all duration-200 ${
                    plan.popular
                      ? "bg-white text-accent-blue hover:bg-white/90 shadow-lg"
                      : "bg-accent-blue hover:bg-accent-blue/90 text-white cta-hover cta-hover-no-ring"
                  }`}
                >
                  {t("pricing.getStarted")}
                </Button>
              </Link>
            ) : null}
          </div>
        ))}
      </div>

      <ServiceModal
        service={selectedService}
        onClose={() => setSelectedService(null)}
        t={t}
      />
    </>
  );
};

export default PricingCards;
